import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Copy } from "lucide-react";

const TURNO_DEFECTO = { inicio: "07:00", fin: "15:00" };
export const MAX_DIAS_PATRON = 14;

export function clavesDiasPatron(n) {
  const num = Math.max(1, Math.min(Number(n) || 5, MAX_DIAS_PATRON));
  return Array.from({ length: num }, (_, i) => `d${i + 1}`);
}

function franjasDe(cfg) {
  if (!cfg) return [];
  if (cfg.turnos?.length) return cfg.turnos.filter((t) => t?.inicio && t?.fin).map((t) => ({ inicio: t.inicio, fin: t.fin }));
  if (cfg.inicio && cfg.fin) return [{ inicio: cfg.inicio, fin: cfg.fin }];
  return [];
}

/** Detecta formato por día de ciclo (d1, d2…) frente a semanal (lun…dom). */
export function esHorarioPatronDias(h) {
  if (!h || typeof h !== "object") return false;
  return Object.keys(h).some((k) => /^d\d+$/.test(k));
}

/**
 * Normaliza horario de un turno de patrón a d1..dn.
 * Migra lun–vie → d1–d5 si viene formato semanal antiguo.
 */
export function normalizarHorarioPatronDias(horario, nDias = 5) {
  const keys = clavesDiasPatron(nDias);
  const src = horario && typeof horario === "object" ? horario : {};
  const tienePatron = keys.some((k) => src[k]);
  const WEEK = ["lun", "mar", "mie", "jue", "vie", "sab", "dom"];

  let plantilla = [{ ...TURNO_DEFECTO }];
  if (tienePatron) {
    for (const k of keys) {
      const fr = franjasDe(src[k]);
      if (fr.length) {
        plantilla = fr;
        break;
      }
    }
  } else {
    for (const k of WEEK) {
      const fr = franjasDe(src[k]);
      if (fr.length) {
        plantilla = fr;
        break;
      }
    }
  }

  const out = {};
  keys.forEach((k, i) => {
    let fr = franjasDe(src[k]);
    if (!fr.length && !tienePatron && WEEK[i]) {
      fr = franjasDe(src[WEEK[i]]);
    }
    if (!fr.length) fr = plantilla.map((t) => ({ ...t }));
    out[k] = { activo: true, turnos: fr };
  });
  return out;
}

export function horarioPatronDiasParaGuardar(horario, nDias = 5) {
  const n = normalizarHorarioPatronDias(horario, nDias);
  const out = {};
  for (const k of clavesDiasPatron(nDias)) {
    out[k] = { activo: true, turnos: n[k].turnos };
  }
  return out;
}

export function unificarTramoPatron(horario, nDias = 5) {
  const n = normalizarHorarioPatronDias(horario, nDias);
  const keys = clavesDiasPatron(nDias);
  const base = n[keys[0]];
  const out = {};
  for (const k of keys) {
    out[k] = {
      activo: true,
      turnos: (base?.turnos || [{ ...TURNO_DEFECTO }]).map((t) => ({ inicio: t.inicio, fin: t.fin })),
    };
  }
  return out;
}

export function resumenHorarioPatronDias(horario, nDias = 5) {
  const n = normalizarHorarioPatronDias(horario, nDias);
  const keys = clavesDiasPatron(nDias);
  const ref = n[keys[0]];
  const tramos = (ref?.turnos || []).map((t) => `${t.inicio}–${t.fin}`).join(", ");
  return `${keys.length} días · ${tramos || "—"}`;
}

export function horarioPatronDiasDefecto(nDias = 5) {
  return normalizarHorarioPatronDias(null, nDias);
}

/** Editor de horas por día del ciclo (Día 1…Día N), no por día de la semana. */
export function HorarioPatronDiasEditor({
  horario,
  onChange,
  diasTrabajo = 5,
  prefix = "patron-dia",
  maxTurnosPorDia = null,
  tramoVariable = false,
}) {
  const nDias = Math.max(1, Math.min(Number(diasTrabajo) || 5, MAX_DIAS_PATRON));
  const h = normalizarHorarioPatronDias(horario, nDias);
  const keys = clavesDiasPatron(nDias);

  const setDia = (k, patch) => {
    if (!tramoVariable) {
      const base = { ...h[keys[0]], ...patch };
      const out = {};
      for (const key of keys) {
        out[key] = {
          activo: true,
          turnos: (base.turnos || []).map((t) => ({ inicio: t.inicio, fin: t.fin })),
        };
      }
      onChange(out);
      return;
    }
    onChange({ ...h, [k]: { ...h[k], ...patch } });
  };

  const setTurno = (k, idx, campo, valor) => {
    const turnos = h[k].turnos.map((t, i) => (i === idx ? { ...t, [campo]: valor } : t));
    setDia(k, { turnos });
  };

  const addTurno = (k) => {
    if (maxTurnosPorDia && h[k].turnos.length >= maxTurnosPorDia) return;
    setDia(k, { turnos: [...h[k].turnos, { inicio: "15:00", fin: "19:00" }] });
  };

  const removeTurno = (k, idx) => {
    if (h[k].turnos.length <= 1) return;
    setDia(k, { turnos: h[k].turnos.filter((_, i) => i !== idx) });
  };

  const copiarDia1 = () => {
    const d1 = h.d1;
    if (!d1) return;
    const out = { ...h };
    for (const k of keys) {
      if (k === "d1") continue;
      out[k] = {
        activo: true,
        turnos: d1.turnos.map((t) => ({ inicio: t.inicio, fin: t.fin })),
      };
    }
    onChange(out);
  };

  const diasVisibles = tramoVariable ? keys : keys.slice(0, 1);

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-zinc-500">
        {tramoVariable
          ? `Horario distinto en cada día de trabajo de este turno (Día 1…Día ${nDias}).`
          : `El mismo tramo horario en los ${nDias} días de trabajo de este turno.`}
      </p>
      {tramoVariable && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-xs"
          onClick={copiarDia1}
          data-testid={`${prefix}-copiar-d1`}
        >
          <Copy className="h-3.5 w-3.5" />
          Copiar Día 1 al resto
        </Button>
      )}
      {diasVisibles.map((k, i) => (
        <div key={k} className="rounded-lg border border-zinc-100 bg-zinc-50/50 px-3 py-2">
          <div className="mb-2 flex items-center justify-between gap-2">
            <Label className="text-sm font-medium text-zinc-800">
              {tramoVariable ? `Día ${i + 1}` : "Todos los días de trabajo"}
            </Label>
            {(!maxTurnosPorDia || h[k].turnos.length < maxTurnosPorDia) && (
              <button
                type="button"
                onClick={() => addTurno(k)}
                className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800"
              >
                <Plus className="h-3.5 w-3.5" /> Tramo
              </button>
            )}
          </div>
          <div className="space-y-1.5">
            {h[k].turnos.map((t, ti) => (
              <div key={ti} className="flex flex-wrap items-center gap-2">
                <Input
                  type="time"
                  value={t.inicio}
                  onChange={(e) => setTurno(k, ti, "inicio", e.target.value)}
                  className="w-[120px]"
                  data-testid={`${prefix}-${k}-inicio-${ti}`}
                />
                <span className="text-zinc-400">–</span>
                <Input
                  type="time"
                  value={t.fin}
                  onChange={(e) => setTurno(k, ti, "fin", e.target.value)}
                  className="w-[120px]"
                  data-testid={`${prefix}-${k}-fin-${ti}`}
                />
                {h[k].turnos.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTurno(k, ti)}
                    className="rounded p-1 text-zinc-400 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
